import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../useAuth";
import { canAccessAdminModules } from "../../accessControl";
import {
  getIncidentAdminTaxonomy,
  getIncidentNotificationRoutingConfig,
  getIncidentWorkflowConfig,
  updateIncidentAdminTaxonomy,
  updateIncidentNotificationRoutingConfig,
  updateIncidentWorkflowConfig,
} from "../../api/incidents.api";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import { spacing } from "../../ui/tokens";

const taxonomyDimensions = ["incident_type", "category", "severity", "priority"];

const defaultTaxonomyConfig = {
  incident_type: [
    { code: "Operational", label: "Operational", display_order: 0, active: true, description: "", is_system: true },
    { code: "Security", label: "Security", display_order: 1, active: true, description: "", is_system: true },
    { code: "Privacy", label: "Privacy", display_order: 2, active: true, description: "", is_system: true },
    { code: "Compliance", label: "Compliance", display_order: 3, active: true, description: "", is_system: true },
  ],
  category: [
    { code: "IT", label: "IT", display_order: 0, active: true, description: "", is_system: true },
    { code: "Security", label: "Security", display_order: 1, active: true, description: "", is_system: true },
    { code: "ThirdParty", label: "Third Party", display_order: 2, active: true, description: "", is_system: true },
    { code: "Operations", label: "Operations", display_order: 3, active: true, description: "", is_system: true },
  ],
  severity: [
    { code: "Low", label: "Low", display_order: 0, active: true, description: "", is_system: true },
    { code: "Medium", label: "Medium", display_order: 1, active: true, description: "", is_system: true },
    { code: "High", label: "High", display_order: 2, active: true, description: "", is_system: true },
    { code: "Critical", label: "Critical", display_order: 3, active: true, description: "", is_system: true },
  ],
  priority: [
    { code: "Low", label: "Low", display_order: 0, active: true, description: "", is_system: true },
    { code: "Medium", label: "Medium", display_order: 1, active: true, description: "", is_system: true },
    { code: "High", label: "High", display_order: 2, active: true, description: "", is_system: true },
    { code: "Critical", label: "Critical", display_order: 3, active: true, description: "", is_system: true },
  ],
};

const defaultWorkflowConfig = {
  statuses: [
    { code: "New", label: "New", display_order: 0, active: true, is_terminal: false, description: "", is_system: true },
    { code: "Investigating", label: "Investigating", display_order: 1, active: true, is_terminal: false, description: "", is_system: true },
    { code: "Mitigated", label: "Mitigated", display_order: 2, active: true, is_terminal: false, description: "", is_system: true },
    { code: "Closed", label: "Closed", display_order: 3, active: true, is_terminal: true, description: "", is_system: true },
  ],
  transitions: [
    { from_status: "New", to_status: "Investigating", required_fields: [], active: true, is_system: true },
    { from_status: "New", to_status: "Mitigated", required_fields: [], active: true, is_system: true },
    { from_status: "Investigating", to_status: "New", required_fields: [], active: true, is_system: true },
    { from_status: "Investigating", to_status: "Mitigated", required_fields: [], active: true, is_system: true },
    { from_status: "Mitigated", to_status: "New", required_fields: [], active: true, is_system: true },
    { from_status: "Mitigated", to_status: "Investigating", required_fields: [], active: true, is_system: true },
  ],
};

const defaultNotificationRouting = {
  routes: [
    { event_type: "incident.created", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.assigned", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.status_changed", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.sla_breached", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.escalated", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.escalated", enabled: true, recipient_type: "escalation_target", recipient_identifier: "", channel: "in_app", active: true, display_order: 1, is_system: true },
    { event_type: "incident.closed", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
    { event_type: "incident.reopened", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: 0, is_system: true },
  ],
};

const taxonomyLabel = (dimension) =>
  ({
    incident_type: "Incident Type",
    category: "Category",
    severity: "Severity",
    priority: "Priority",
  }[dimension] || dimension);

const extractApiError = (err, fallback) => {
  const source = err?.error || err?.detail || err;
  if (typeof source === "string" && source.trim()) return source;
  if (typeof source?.detail === "string" && source.detail.trim()) return source.detail;
  if (source?.field_errors && typeof source.field_errors === "object") {
    const message = Object.values(source.field_errors).find((value) => typeof value === "string" && value.trim());
    if (message) return message;
  }
  return fallback;
};

export default function IncidentAdmin() {
  const { user } = useAuth();
  const canManageAdminModules = canAccessAdminModules(user);
  const [adminConfig, setAdminConfig] = useState({
    taxonomy: defaultTaxonomyConfig,
    workflow: defaultWorkflowConfig,
    notificationRouting: defaultNotificationRouting,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const activeTaxonomyCount = useMemo(
    () =>
      taxonomyDimensions.reduce(
        (count, dimension) => count + (adminConfig.taxonomy?.[dimension] || []).filter((item) => item.active).length,
        0
      ),
    [adminConfig.taxonomy]
  );

  const activeWorkflowCount = useMemo(
    () => (adminConfig.workflow?.statuses || []).filter((item) => item.active).length,
    [adminConfig.workflow]
  );

  const activeNotificationRouteCount = useMemo(
    () => (adminConfig.notificationRouting?.routes || []).filter((item) => item.active && item.enabled).length,
    [adminConfig.notificationRouting]
  );

  const loadConfig = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getIncidentAdminTaxonomy("incident_type"),
      getIncidentAdminTaxonomy("category"),
      getIncidentAdminTaxonomy("severity"),
      getIncidentAdminTaxonomy("priority"),
      getIncidentWorkflowConfig(),
      getIncidentNotificationRoutingConfig(),
    ])
      .then(([typeRes, categoryRes, severityRes, priorityRes, workflowRes, notificationRes]) => {
        setAdminConfig({
          taxonomy: {
            incident_type: typeRes.data || typeRes,
            category: categoryRes.data || categoryRes,
            severity: severityRes.data || severityRes,
            priority: priorityRes.data || priorityRes,
          },
          workflow: workflowRes.data || workflowRes,
          notificationRouting: notificationRes.data || notificationRes,
        });
      })
      .catch((err) => {
        setAdminConfig({
          taxonomy: defaultTaxonomyConfig,
          workflow: defaultWorkflowConfig,
          notificationRouting: defaultNotificationRouting,
        });
        setError(extractApiError(err, "Unable to load Incident settings."));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!canManageAdminModules) return;
    loadConfig();
  }, [canManageAdminModules, loadConfig]);

  const updateTaxonomyRow = (dimension, index, field, value) => {
    setAdminConfig((prev) => ({
      ...prev,
      taxonomy: {
        ...prev.taxonomy,
        [dimension]: (prev.taxonomy?.[dimension] || []).map((item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const addTaxonomyRow = (dimension) => {
    setAdminConfig((prev) => ({
      ...prev,
      taxonomy: {
        ...prev.taxonomy,
        [dimension]: [
          ...(prev.taxonomy?.[dimension] || []),
          { code: "", label: "", description: "", display_order: (prev.taxonomy?.[dimension] || []).length, active: true, is_system: false },
        ],
      },
    }));
  };

  const saveTaxonomy = async (dimension) => {
    setSaving(true);
    setActionMessage(null);
    try {
      const payload = (adminConfig.taxonomy?.[dimension] || []).map((item, index) => ({
        code: String(item.code || "").trim(),
        label: String(item.label || "").trim(),
        description: item.description || null,
        display_order: Number(item.display_order ?? index),
        active: Boolean(item.active),
        is_system: Boolean(item.is_system),
      }));
      const res = await updateIncidentAdminTaxonomy(dimension, payload);
      setAdminConfig((prev) => ({
        ...prev,
        taxonomy: { ...prev.taxonomy, [dimension]: res.data || res },
      }));
      setActionMessage({ tone: "success", text: `${taxonomyLabel(dimension)} settings saved.` });
    } catch (err) {
      setActionMessage({ tone: "error", text: extractApiError(err, `Failed to save ${taxonomyLabel(dimension).toLowerCase()} settings.`) });
    } finally {
      setSaving(false);
    }
  };

  const updateWorkflowStatusRow = (index, field, value) => {
    setAdminConfig((prev) => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        statuses: (prev.workflow?.statuses || []).map((item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const addWorkflowStatusRow = () => {
    setAdminConfig((prev) => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        statuses: [
          ...(prev.workflow?.statuses || []),
          { code: "", label: "", description: "", display_order: (prev.workflow?.statuses || []).length, active: true, is_terminal: false, is_system: false },
        ],
      },
    }));
  };

  const updateWorkflowTransitionRow = (index, field, value) => {
    setAdminConfig((prev) => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        transitions: (prev.workflow?.transitions || []).map((item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const addWorkflowTransitionRow = () => {
    setAdminConfig((prev) => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        transitions: [
          ...(prev.workflow?.transitions || []),
          { from_status: "", to_status: "", required_fields: [], active: true, is_system: false },
        ],
      },
    }));
  };

  const saveWorkflow = async () => {
    setSaving(true);
    setActionMessage(null);
    try {
      const payload = {
        statuses: (adminConfig.workflow?.statuses || []).map((item, index) => ({
          code: String(item.code || "").trim(),
          label: String(item.label || "").trim(),
          description: item.description || null,
          display_order: Number(item.display_order ?? index),
          active: Boolean(item.active),
          is_terminal: Boolean(item.is_terminal),
          is_system: Boolean(item.is_system),
        })),
        transitions: (adminConfig.workflow?.transitions || []).map((item) => ({
          from_status: String(item.from_status || "").trim(),
          to_status: String(item.to_status || "").trim(),
          required_fields: Array.isArray(item.required_fields)
            ? item.required_fields
            : String(item.required_fields || "")
                .split(",")
                .map((value) => value.trim())
                .filter(Boolean),
          active: Boolean(item.active),
          is_system: Boolean(item.is_system),
        })),
      };
      const res = await updateIncidentWorkflowConfig(payload);
      setAdminConfig((prev) => ({ ...prev, workflow: res.data || res }));
      setActionMessage({ tone: "success", text: "Workflow settings saved." });
    } catch (err) {
      setActionMessage({ tone: "error", text: extractApiError(err, "Failed to save workflow settings.") });
    } finally {
      setSaving(false);
    }
  };

  const updateNotificationRouteRow = (index, field, value) => {
    setAdminConfig((prev) => ({
      ...prev,
      notificationRouting: {
        ...prev.notificationRouting,
        routes: (prev.notificationRouting?.routes || []).map((item, itemIndex) =>
          itemIndex === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const addNotificationRouteRow = () => {
    setAdminConfig((prev) => ({
      ...prev,
      notificationRouting: {
        ...prev.notificationRouting,
        routes: [
          ...(prev.notificationRouting?.routes || []),
          { event_type: "incident.created", enabled: true, recipient_type: "owner", recipient_identifier: "", channel: "in_app", active: true, display_order: (prev.notificationRouting?.routes || []).length, is_system: false },
        ],
      },
    }));
  };

  const saveNotificationRouting = async () => {
    setSaving(true);
    setActionMessage(null);
    try {
      const payload = {
        routes: (adminConfig.notificationRouting?.routes || []).map((item, index) => ({
          event_type: item.event_type,
          enabled: Boolean(item.enabled),
          recipient_type: item.recipient_type,
          recipient_identifier: item.recipient_identifier || null,
          channel: item.channel || "in_app",
          active: Boolean(item.active),
          display_order: Number(item.display_order ?? index),
          is_system: Boolean(item.is_system),
        })),
      };
      const res = await updateIncidentNotificationRoutingConfig(payload);
      setAdminConfig((prev) => ({ ...prev, notificationRouting: res.data || res }));
      setActionMessage({ tone: "success", text: "Notification routing saved." });
    } catch (err) {
      setActionMessage({ tone: "error", text: extractApiError(err, "Failed to save notification routing.") });
    } finally {
      setSaving(false);
    }
  };

  if (!canManageAdminModules) {
    return (
      <div style={{ display: "grid", gap: spacing.lg }}>
        <h2 style={{ margin: 0 }}>Incident Module Settings</h2>
        <div style={guardStyle}>You need Manage access to Admin Module Portal to manage Incident configuration.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: spacing.lg * 2 }}>
      <section style={heroStyle}>
        <h2 style={{ margin: 0 }}>Incident Module Settings</h2>
        <p style={heroCopyStyle}>
          Manage module-level taxonomy, workflow, and notification routing for incidents from the Admin Module Portal.
        </p>
      </section>

      <section style={summaryGridStyle}>
        <SummaryCard label="Active taxonomy values" value={activeTaxonomyCount} helpText="Across incident type, category, severity, and priority." />
        <SummaryCard label="Active workflow statuses" value={activeWorkflowCount} helpText="Statuses currently available to incident records." />
        <SummaryCard label="Enabled routes" value={activeNotificationRouteCount} helpText="Notification routes that are active and enabled." />
      </section>

      {error ? <div style={errorStyle}>{error}</div> : null}
      {actionMessage ? (
        <div style={actionMessage.tone === "error" ? errorStyle : successStyle}>{actionMessage.text}</div>
      ) : null}
      {loading ? <div>Loading Incident settings...</div> : null}

      {!loading && (
        <>
          <section style={cardStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={{ margin: 0 }}>Taxonomy</h3>
                <p style={sectionDescriptionStyle}>
                  Configure the incident classifications consumed by incident forms and filters.
                </p>
              </div>
            </div>
            <div style={{ display: "grid", gap: spacing.lg }}>
              {taxonomyDimensions.map((dimension) => (
                <div key={dimension} style={nestedCardStyle}>
                  <div style={subSectionHeaderStyle}>
                    <div style={{ fontWeight: 600 }}>{taxonomyLabel(dimension)}</div>
                    <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                      <Button onClick={() => addTaxonomyRow(dimension)} variant="secondary">Add</Button>
                      <Button onClick={() => saveTaxonomy(dimension)} variant="secondary" disabled={saving}>Save</Button>
                    </div>
                  </div>
                  {(adminConfig.taxonomy?.[dimension] || []).map((item, index) => (
                    <div key={`${dimension}-${index}`} style={taxonomyRowStyle}>
                      <Input
                        value={item.code || ""}
                        onChange={(e) => updateTaxonomyRow(dimension, index, "code", e.target.value)}
                        placeholder="Code"
                      />
                      <Input
                        value={item.label || ""}
                        onChange={(e) => updateTaxonomyRow(dimension, index, "label", e.target.value)}
                        placeholder="Label"
                      />
                      <Input
                        type="number"
                        value={item.display_order ?? index}
                        onChange={(e) => updateTaxonomyRow(dimension, index, "display_order", e.target.value)}
                        placeholder="Order"
                      />
                      <label style={checkboxLabelStyle}>
                        <input
                          type="checkbox"
                          checked={Boolean(item.active)}
                          onChange={(e) => updateTaxonomyRow(dimension, index, "active", e.target.checked)}
                        />
                        Active
                      </label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section style={cardStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={{ margin: 0 }}>Workflow</h3>
                <p style={sectionDescriptionStyle}>
                  Define the statuses and transitions available within the incident response lifecycle.
                </p>
              </div>
              <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                <Button onClick={addWorkflowStatusRow} variant="secondary">Add Status</Button>
                <Button onClick={addWorkflowTransitionRow} variant="secondary">Add Transition</Button>
                <Button onClick={saveWorkflow} variant="secondary" disabled={saving}>Save Workflow</Button>
              </div>
            </div>

            <div style={{ display: "grid", gap: spacing.md }}>
              {(adminConfig.workflow?.statuses || []).map((item, index) => (
                <div key={`status-${index}`} style={workflowStatusRowStyle}>
                  <Input value={item.code || ""} onChange={(e) => updateWorkflowStatusRow(index, "code", e.target.value)} placeholder="Code" />
                  <Input value={item.label || ""} onChange={(e) => updateWorkflowStatusRow(index, "label", e.target.value)} placeholder="Label" />
                  <Input
                    type="number"
                    value={item.display_order ?? index}
                    onChange={(e) => updateWorkflowStatusRow(index, "display_order", e.target.value)}
                    placeholder="Order"
                  />
                  <label style={checkboxLabelStyle}>
                    <input type="checkbox" checked={Boolean(item.active)} onChange={(e) => updateWorkflowStatusRow(index, "active", e.target.checked)} />
                    Active
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input type="checkbox" checked={Boolean(item.is_terminal)} onChange={(e) => updateWorkflowStatusRow(index, "is_terminal", e.target.checked)} />
                    Terminal
                  </label>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: spacing.md, marginTop: spacing.lg }}>
              {(adminConfig.workflow?.transitions || []).map((item, index) => (
                <div key={`transition-${index}`} style={workflowTransitionRowStyle}>
                  <Input value={item.from_status || ""} onChange={(e) => updateWorkflowTransitionRow(index, "from_status", e.target.value)} placeholder="From status" />
                  <Input value={item.to_status || ""} onChange={(e) => updateWorkflowTransitionRow(index, "to_status", e.target.value)} placeholder="To status" />
                  <Input
                    value={Array.isArray(item.required_fields) ? item.required_fields.join(", ") : item.required_fields || ""}
                    onChange={(e) => updateWorkflowTransitionRow(index, "required_fields", e.target.value)}
                    placeholder="Required fields"
                  />
                  <label style={checkboxLabelStyle}>
                    <input type="checkbox" checked={Boolean(item.active)} onChange={(e) => updateWorkflowTransitionRow(index, "active", e.target.checked)} />
                    Active
                  </label>
                </div>
              ))}
            </div>
          </section>

          <section style={cardStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={{ margin: 0 }}>Notification Routing</h3>
                <p style={sectionDescriptionStyle}>
                  Configure who is notified during incident lifecycle events and escalation changes.
                </p>
              </div>
              <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                <Button onClick={addNotificationRouteRow} variant="secondary">Add Route</Button>
                <Button onClick={saveNotificationRouting} variant="secondary" disabled={saving}>Save Routing</Button>
              </div>
            </div>

            <div style={{ display: "grid", gap: spacing.md }}>
              {(adminConfig.notificationRouting?.routes || []).map((item, index) => (
                <div key={`route-${index}`} style={routeRowStyle}>
                  <Input
                    value={item.event_type || ""}
                    onChange={(e) => updateNotificationRouteRow(index, "event_type", e.target.value)}
                    placeholder="Event type"
                  />
                  <Select value={item.recipient_type || "owner"} onChange={(e) => updateNotificationRouteRow(index, "recipient_type", e.target.value)}>
                    <option value="owner">owner</option>
                    <option value="actor">actor</option>
                    <option value="role">role</option>
                    <option value="user">user</option>
                    <option value="escalation_target">escalation_target</option>
                  </Select>
                  <Input
                    value={item.recipient_identifier || ""}
                    onChange={(e) => updateNotificationRouteRow(index, "recipient_identifier", e.target.value)}
                    placeholder="Recipient identifier"
                  />
                  <Input
                    type="number"
                    value={item.display_order ?? index}
                    onChange={(e) => updateNotificationRouteRow(index, "display_order", e.target.value)}
                    placeholder="Order"
                  />
                  <label style={checkboxLabelStyle}>
                    <input type="checkbox" checked={Boolean(item.enabled)} onChange={(e) => updateNotificationRouteRow(index, "enabled", e.target.checked)} />
                    Enabled
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input type="checkbox" checked={Boolean(item.active)} onChange={(e) => updateNotificationRouteRow(index, "active", e.target.checked)} />
                    Active
                  </label>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, helpText }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value}</div>
      {helpText ? <div style={summaryHelpStyle}>{helpText}</div> : null}
    </div>
  );
}

const heroStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  display: "grid",
  gap: spacing.sm,
};

const heroCopyStyle = {
  margin: 0,
  color: "#6b7280",
  lineHeight: 1.5,
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: spacing.lg,
};

const summaryCardStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  display: "grid",
  gap: spacing.xs,
};

const summaryLabelStyle = {
  fontSize: 12,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const summaryValueStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
};

const summaryHelpStyle = {
  fontSize: 13,
  color: "#6b7280",
};

const cardStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  display: "grid",
  gap: spacing.lg,
};

const nestedCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: 12,
  display: "grid",
  gap: spacing.md,
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.lg,
  flexWrap: "wrap",
};

const subSectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
};

const sectionDescriptionStyle = {
  margin: "6px 0 0",
  fontSize: 13,
  color: "#6b7280",
  lineHeight: 1.5,
};

const taxonomyRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: spacing.md,
  alignItems: "center",
};

const workflowStatusRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: spacing.md,
  alignItems: "center",
};

const workflowTransitionRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: spacing.md,
  alignItems: "center",
};

const routeRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: spacing.md,
  alignItems: "center",
};

const checkboxLabelStyle = {
  display: "flex",
  gap: spacing.sm,
  alignItems: "center",
  fontSize: 13,
};

const successStyle = {
  color: "#166534",
  background: "#ecfdf5",
  border: "1px solid #bbf7d0",
  borderRadius: 10,
  padding: 12,
};

const errorStyle = {
  color: "#b91c1c",
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: 10,
  padding: 12,
};

const guardStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  color: "#6b7280",
};
